import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Minimal fetch-on-mount hook with a manual `reload`.
 *
 * The console is read-then-mutate-then-reload throughout, and pulling in a
 * data-fetching library for that would be more machinery than the pattern
 * needs. Errors are already surfaced as toasts by the axios layer, so this only
 * has to track loading and the last good value.
 */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);

  // Kept in a ref so `run` doesn't change identity every render just because
  // the caller passed an inline arrow function.
  const fnRef = useRef(fn);
  fnRef.current = fn;

  // Guards against a slow earlier request resolving after a newer one and
  // overwriting fresher data.
  const requestId = useRef(0);

  const run = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const result = await fnRef.current();
      if (id === requestId.current) setData(result);
    } catch {
      /* toast already shown by the axios interceptor */
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, reload: run };
}
